/**
 * The conversation inside a library.
 *
 * *** ONE SHARED ROOM, VISIBLE ONLY FROM INSIDE ***
 * Rendered only on the card the viewer is sitting in. The server refuses
 * both reads and writes from outside, so this is not the security boundary
 * — it is the reason the screen never asks for a room it cannot have.
 *
 * *** A MENTOR'S LINE IS MARKED ***
 * In a room of twenty students the one answer that matters is the mentor's.
 * Their name carries a badge and their bubble a border, so it is findable
 * without reading every line.
 */

import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { ApiError } from '@/lib/api'
import { useLibraryMessages, usePostLibraryMessage } from '@/lib/queries'
import type { RoomMessage } from '@/types/api'
import type { ChatPlacement } from './useChatPlacement'
import styles from './LibraryRoom.module.css'

/** Matches the server's column and its validation. */
const MAX_BODY = 4000

/** Clock time, which is all a same-day room conversation needs. */
function timeOf(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  return at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * True when this message should carry its sender's name.
 *
 * Repeating the name on every line of a six-line answer is noise; showing it
 * once per run of consecutive messages from the same person is how every
 * chat worth using reads.
 */
function startsRun(message: RoomMessage, previous: RoomMessage | undefined): boolean {
  return previous === undefined || previous.sender_id !== message.sender_id
}

export function LibraryRoom({
  libraryId,
  placement,
  onPlacement,
  onHide,
}: {
  libraryId: string
  placement: ChatPlacement
  onPlacement: (next: ChatPlacement) => void
  /*
    Absent when the chat is pinned: a pinned chat is part of the column and
    collapsing it there would leave an empty stub, so the control is not
    rendered rather than rendered and ignored.
  */
  onHide?: () => void
}) {
  const { data, isLoading, error } = useLibraryMessages(libraryId)
  const post = usePostLibraryMessage(libraryId)

  const [draft, setDraft] = useState('')
  const [sendError, setSendError] = useState<string | null>(null)

  const messages = data?.messages ?? []

  /*
    *** SCROLL ONLY WHEN THE COUNT CHANGES ***
    The list refetches on a timer. Scrolling on every render would yank the
    view out from under someone reading back through the conversation, every
    eight seconds.
  */
  const endRef = useRef<HTMLDivElement | null>(null)
  const seen = useRef(0)

  useEffect(() => {
    if (messages.length === seen.current) return
    seen.current = messages.length
    endRef.current?.scrollIntoView({ block: 'nearest' })
  }, [messages.length])

  const handleSend = async (event: FormEvent) => {
    event.preventDefault()
    const body = draft.trim()
    if (!body) return

    setSendError(null)
    try {
      await post.mutateAsync(body)
      // Cleared only on success: a failed send that wiped the box would
      // lose what the student typed.
      setDraft('')
    } catch (caught) {
      setSendError(
        caught instanceof ApiError ? caught.message : 'Could not send that.',
      )
    }
  }

  const tooLong = draft.length > MAX_BODY

  return (
    <section className={styles.room} aria-label="Library conversation">
      <div className={styles.head}>
        <h4 className={styles.title}>Room chat</h4>

        {/*
          *** ICONS, SO EACH CARRIES A TOOLTIP ***
          An icon alone is a guess until you click it. The tooltip is drawn
          by the page rather than left to `title`, which waits a second and
          then appears wherever the cursor happens to be.
        */}
        <div className={styles.controls}>
          {placement === 'dock' ? (
            <button
              type="button"
              className={styles.iconBtn}
              data-tip="Pin to top of the room"
              aria-label="Pin chat to top of the room"
              onClick={() => onPlacement('pin')}
            >
              ⇱
            </button>
          ) : (
            <button
              type="button"
              className={styles.iconBtn}
              data-tip="Dock to bottom right"
              aria-label="Dock chat to bottom right"
              onClick={() => onPlacement('dock')}
            >
              ⇲
            </button>
          )}

          {onHide && (
            <button
              type="button"
              className={styles.iconBtn}
              data-tip="Hide the chat"
              aria-label="Hide the chat"
              onClick={onHide}
            >
              ▾
            </button>
          )}
        </div>
      </div>

      <div className={styles.log} role="log" aria-live="polite">
        {isLoading && !data && <p className={styles.muted}>Loading…</p>}

        {error && (
          <p className={styles.error}>
            {error instanceof ApiError
              ? error.message
              : 'Could not load the conversation.'}
          </p>
        )}

        {!isLoading && !error && messages.length === 0 && (
          <p className={styles.muted}>
            No messages yet. Say hello to the room.
          </p>
        )}

        {messages.map((message, index) => {
          const isMentor = message.sender_role === 'MENTOR'
          const named = startsRun(message, messages[index - 1])

          return (
            <div
              key={message.message_id}
              className={`${styles.line} ${message.is_mine ? styles.mine : ''}`}
            >
              {named && (
                <span className={styles.sender}>
                  {message.is_mine ? 'You' : message.sender_name}
                  {isMentor && <span className={styles.badge}>Mentor</span>}
                </span>
              )}
              <div
                className={`${styles.bubble} ${isMentor ? styles.bubbleMentor : ''}`}
              >
                <span className={styles.body}>{message.body}</span>
                <time className={styles.time} dateTime={message.sent_at}>
                  {timeOf(message.sent_at)}
                </time>
              </div>
            </div>
          )
        })}

        <div ref={endRef} />
      </div>

      <form onSubmit={handleSend} className={styles.composer}>
        <input
          className={styles.input}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Message the room…"
          aria-label="Message the room"
          disabled={post.isPending}
          maxLength={MAX_BODY}
        />
        <Button
          type="submit"
          size="sm"
          loading={post.isPending}
          disabled={!draft.trim() || tooLong}
        >
          Send
        </Button>
      </form>

      {sendError && <p className={styles.error}>{sendError}</p>}
    </section>
  )
}
